'use client';

import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { ClickAwayListener } from 'src/components';
import { useProfile } from 'src/context/providers/ProfileProvider';
import useGetPayerMixReimbursement from 'src/hooks/payerMix/useGetPayerMixReimbursement';
import useUserSettings from 'src/hooks/users/useUserSettings';
import { useParams } from 'next/navigation';
import { SOC_TYPE_LABELS } from 'src/components/Profiles/components/ProfileDetails/socTypes';

interface SiteOption {
  value: string;
  label: string;
}

const AllSitesSelect = () => {
  const { type } = useParams<{ type: string }>();

  const { settings: userSettings, updateSettings } = useUserSettings();
  const { entityDetails } = useProfile();

  const [open, setOpen] = useState(false);

  const selectedSite = userSettings?.selectedSite ?? null;

  const { data: reimbursementData } = useGetPayerMixReimbursement({
    providerId: entityDetails?.provider_id,
    location: type,
  });

  const siteOptions = useMemo<SiteOption[]>(() => {
    if (!reimbursementData?.results) return [];

    // Collect all places of service for the selected payer
    const allPayersWithMatchingName = reimbursementData.results
      .flatMap((result) =>
        result.payers.filter(
          (payer) =>
            payer.payer_name === userSettings?.selectedPayer?.payer_name
        )
      )
      .filter(Boolean);

    // Get unique site of care types
    const uniqueSocTypes = Array.from(
      new Set(
        allPayersWithMatchingName.map((payer: any) => payer.soc_type)
      )
    );

    // Format as options with value and label
    return uniqueSocTypes.map((code: string) => {
      return {
        value: code,
        label: SOC_TYPE_LABELS[code] || code,
      };
    });
  }, [reimbursementData, userSettings?.selectedPayer]);

  const hasOptions = useMemo(() => {
    return siteOptions.length > 0;
  }, [siteOptions]);

  const displayLabel = useMemo(() => {
    if (!hasOptions) return 'All Sites';
    return (
      siteOptions.find((option) => option.value === selectedSite)?.label ??
      'All Sites'
    );
  }, [selectedSite, siteOptions, hasOptions]);

  const toggleOption = (value: string) => {
    updateSettings({
      selectedSite: value,
    });
    setOpen(false);
  };

  const disabled = !userSettings?.selectedPayer || !hasOptions;
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="relative">
        <button
          data-testid="all-sites-select-button"
          type="button"
          onClick={() => setOpen(!open)}
          disabled={disabled}
          className="flex items-center gap-2 py-1.5 px-3 max-w-64 h-8 border border-neutral-300 rounded-lg bg-white whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="text-sm font-medium text-gray-700 ">Site:</span>
          <span className="text-sm font-medium text-gray-700 overflow-hidden text-ellipsis">
            {displayLabel ? displayLabel : 'All Sites'}
          </span>
          <Icon
            icon={faCaretDown}
            className={`${
              disabled ? 'text-gray-400' : 'text-gray-500'
            } shrink-0`}
          />
        </button>
        {open && (
          <div className="absolute z-10 mt-1 min-w-48 max-h-64 overflow-y-auto bg-white border border-neutral-300 rounded-lg shadow-md">
            <div className="flex flex-col gap-2 p-4">
              {siteOptions?.map((option: SiteOption) => (
                <label
                  key={option.value}
                  data-testid={`all-sites-select-option-${option.value}`}
                  className="flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  onClick={() => toggleOption(option.value)}
                >
                  <Icon
                    icon={faCheck}
                    size="sm"
                    className={
                      selectedSite === option.value
                        ? 'text-sky-600'
                        : 'text-transparent'
                    }
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default AllSitesSelect;
